import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import AsyncSelect from 'react-select/async';
import moment from 'moment';

import { Modal } from 'atoms';
import { PopupContainer } from 'molecules';
import { useAppendStudentToGroup, IGroupDetail } from 'core/hooks';
import {
  PaginatedStudentsDocument,
  PaginatedStudentsQuery,
  PaginatedStudentsQueryVariables,
} from 'core/graphql';

import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface IAddStudentToGroupInlinePopupProps {
  group: IGroupDetail;
  onClose: () => void;
  onAdded: () => void;
}

interface IStudentOption {
  value: string;
  label: string;
}

/**
 * Single-shot «+ Студент» from the GroupDetail HeaderBar / StudentsTab.
 * Async-searches the existing students by name/email/phone, then fires
 * `appendStudentToGroup` (TZ §G8) with the picked `studentId` + a
 * minimal `agreementInput` carrying `groupId` and inherited currency.
 *
 * Heavier per-agreement fields (legal address, VAT, payment-by-company,
 * contribution schedule) are intentionally left to the dedicated
 * `EditAgreementPopup` once the row exists — fewer fields here means
 * fewer wrong defaults.
 */
export const AddStudentToGroupInlinePopup = memo(
  ({ group, onClose, onAdded }: IAddStudentToGroupInlinePopupProps) => {
    const { t } = useTranslation();
    const apollo = useApolloClient();
    const { append, pending } = useAppendStudentToGroup(() => {
      onAdded();
      onClose();
    });

    const [student, setStudent] = useState<IStudentOption | null>(null);
    const [date, setDate] = useState<string>(moment().format('YYYY-MM-DD'));
    const [agreementTitle, setAgreementTitle] = useState<string>('');
    const [budget, setBudget] = useState<string>('');

    const loadStudentOptions = useCallback(
      async (input: string): Promise<IStudentOption[]> => {
        try {
          const result = await apollo.query<
            PaginatedStudentsQuery,
            PaginatedStudentsQueryVariables
          >({
            query: PaginatedStudentsDocument,
            variables: { query: input || '', first: 20 },
            fetchPolicy: 'no-cache',
          });
          const edges = result.data?.allStudents?.edges || [];
          return edges
            .map((edge) => edge?.node)
            .filter((node): node is NonNullable<typeof node> => Boolean(node))
            .map((node) => {
              const u = node.user;
              const name =
                [u?.firstName, u?.lastName].filter(Boolean).join(' ') ||
                u?.email ||
                node.id;
              return {
                value: node.id,
                label: `${name}${u?.email ? ` · ${u.email}` : ''}`,
              };
            });
        } catch {
          return [];
        }
      },
      [apollo],
    );

    const canSubmit = Boolean(student && date && !pending);

    const onSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();
        if (!student || !canSubmit) return;
        await append({
          studentId: student.value,
          agreementInput: {
            groupId: group.id,
            // CreateAgreementInputBase requires both — fall back to
            // empty strings so the server uses its defaults rather
            // than 400-ing on a missing field.
            currency: group.currency || '',
            agreementTitle: agreementTitle.trim() || group.name || '',
            date,
            startAgreementBudget: budget ? Number(budget) : undefined,
          },
        });
      },
      [
        student,
        canSubmit,
        append,
        group.id,
        group.currency,
        group.name,
        agreementTitle,
        date,
        budget,
      ],
    );

    return (
      <Modal>
        <PopupContainer
          width="520px"
          title={t('pages.groupDetail.appendStudent.title')}
          onClose={onClose}
        >
          <form onSubmit={onSubmit}>
            <L.FormBody>
              <div style={{ fontSize: 13, color: GroupColors.textSecondary }}>
                {t('pages.groupDetail.appendStudent.hint', {
                  group: group.name,
                  currency: group.currency || '—',
                })}
              </div>

              <L.FormLabel>
                {t('pages.groupDetail.appendStudent.student')}
                <AsyncSelect
                  autoFocus
                  cacheOptions
                  defaultOptions
                  value={student}
                  onChange={(v) => setStudent((v as IStudentOption) || null)}
                  loadOptions={loadStudentOptions}
                  placeholder={t(
                    'pages.groupDetail.appendStudent.searchPlaceholder',
                  )}
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.appendStudent.agreementTitle')}
                <L.FormInput
                  type="text"
                  value={agreementTitle}
                  onChange={(e) => setAgreementTitle(e.target.value)}
                  placeholder={t(
                    'pages.groupDetail.appendStudent.agreementTitlePlaceholder',
                  )}
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.appendStudent.date')}
                <L.FormInput
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </L.FormLabel>

              <L.FormLabel>
                {t('pages.groupDetail.appendStudent.budget')}
                <L.FormInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder={group.price?.toString() || '0.00'}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: GroupColors.textMuted,
                    fontWeight: 500,
                  }}
                >
                  {t('pages.groupDetail.appendStudent.budgetHint', {
                    price: group.price ?? '—',
                    currency: group.currency || '',
                  })}
                </span>
              </L.FormLabel>

              <L.FormFooter>
                <L.PrimaryActionButton
                  type="button"
                  onClick={onClose}
                  disabled={pending}
                >
                  {t('pages.groupDetail.appendStudent.cancel')}
                </L.PrimaryActionButton>
                <L.PrimaryActionButton
                  type="submit"
                  tone="primary"
                  disabled={!canSubmit}
                >
                  {pending
                    ? t('pages.groupDetail.appendStudent.adding')
                    : t('pages.groupDetail.appendStudent.add')}
                </L.PrimaryActionButton>
              </L.FormFooter>
            </L.FormBody>
          </form>
        </PopupContainer>
      </Modal>
    );
  },
);
